import React from "react";

/**
 * Minimal, dependency-free markdown renderer.
 * Renders straight to React elements (no dangerouslySetInnerHTML), so vendor
 * supplied listing markdown can never inject HTML/scripts into the dashboard.
 *
 * Supports: headings, paragraphs, bold/italic/strikethrough, inline code,
 * fenced code blocks, links, ordered/unordered (nested) lists, blockquotes,
 * horizontal rules and pipe tables.
 */

const S = {
  root: {
    color: "#374151",
    fontSize: 14,
    lineHeight: 1.6,
    wordBreak: "break-word",
  },
  p: { margin: "0 0 10px" },
  ul: { margin: "0 0 10px", paddingLeft: 22 },
  li: { margin: "2px 0" },
  code: {
    background: "#eceff2",
    borderRadius: 4,
    padding: "1px 4px",
    fontFamily: "Menlo, Consolas, monospace",
    fontSize: 12.5,
  },
  pre: {
    background: "#eceff2",
    borderRadius: 6,
    padding: 12,
    margin: "0 0 10px",
    overflowX: "auto",
    fontFamily: "Menlo, Consolas, monospace",
    fontSize: 12.5,
  },
  quote: {
    margin: "0 0 10px",
    padding: "4px 0 4px 12px",
    borderLeft: "3px solid #e5e7eb",
    color: "#6b7280",
  },
  hr: { border: 0, borderTop: "1px solid #e5e7eb", margin: "12px 0" },
  link: { color: "#0b5b57", textDecoration: "underline" },
  tableWrap: { overflowX: "auto", margin: "0 0 10px" },
  table: { borderCollapse: "collapse", width: "100%" },
  cell: { border: "1px solid #e5e7eb", padding: "6px 8px", textAlign: "left" },
  th: {
    border: "1px solid #e5e7eb",
    padding: "6px 8px",
    textAlign: "left",
    background: "#f9fafb",
    fontWeight: 600,
  },
};

const headingStyle = (level) => ({
  margin: level === 1 ? "0 0 8px" : "12px 0 6px",
  fontWeight: 600,
  fontSize: [17, 16, 15, 14, 13.5, 13][level - 1],
  color: "#111827",
});

const LIST_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;

const INLINE_PATTERN = [
  "(`[^`]+`)", // inline code
  "(\\*\\*[\\s\\S]+?\\*\\*|__[\\s\\S]+?__)", // bold
  "(\\*[^*\\n]+\\*|_[^_\\n]+_)", // italic
  "(~~[\\s\\S]+?~~)", // strikethrough
  "(!?\\[[^\\]]*\\]\\([^()\\s]*(?:\\([^()]*\\)[^()\\s]*)*(?:\\s+\"[^\"]*\")?\\))", // link / image
  "(https?://[^\\s<>()]+)", // bare url
].join("|");

function parseInline(text, keyPrefix) {
  if (!text) return null;
  // A fresh regex per call: parseInline recurses (bold inside link, etc.) and a
  // shared /g regex would have its lastIndex reset by the nested call.
  const re = new RegExp(INLINE_PATTERN, "g");
  const out = [];
  let last = 0;
  let idx = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const token = match[0];
    const key = `${keyPrefix}-i${idx++}`;

    if (match[1]) {
      out.push(
        <code key={key} style={S.code}>
          {token.slice(1, -1)}
        </code>
      );
    } else if (match[2]) {
      out.push(<strong key={key}>{parseInline(token.slice(2, -2), key)}</strong>);
    } else if (match[3]) {
      out.push(<em key={key}>{parseInline(token.slice(1, -1), key)}</em>);
    } else if (match[4]) {
      out.push(<del key={key}>{parseInline(token.slice(2, -2), key)}</del>);
    } else if (match[5]) {
      const link = /^(!?)\[([^\]]*)\]\((\S*?)(?:\s+"[^"]*")?\)$/.exec(token);
      const [, bang, label, href] = link;
      const safe = /^(https?:|mailto:)/i.test(href) ? href : null;
      if (bang) {
        out.push(
          safe ? (
            <img
              key={key}
              src={safe}
              alt={label}
              style={{ maxWidth: "100%", borderRadius: 6, margin: "4px 0" }}
            />
          ) : (
            label
          )
        );
      } else if (safe) {
        out.push(
          <a key={key} href={safe} target="_blank" rel="noopener noreferrer" style={S.link}>
            {parseInline(label, key) ?? safe}
          </a>
        );
      } else {
        out.push(label);
      }
    } else if (match[6]) {
      // Don't swallow sentence punctuation that trails a bare URL.
      const trailing = /[.,;:!?)\]]+$/.exec(token);
      const href = trailing ? token.slice(0, -trailing[0].length) : token;
      out.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" style={S.link}>
          {href}
        </a>
      );
      if (trailing) out.push(trailing[0]);
    }
    last = match.index + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function withBreaks(lines, keyPrefix) {
  const out = [];
  lines.forEach((line, idx) => {
    if (idx > 0) out.push(<br key={`${keyPrefix}-br${idx}`} />);
    const parsed = parseInline(line, `${keyPrefix}-l${idx}`);
    if (parsed) out.push(<React.Fragment key={`${keyPrefix}-f${idx}`}>{parsed}</React.Fragment>);
  });
  return out;
}

const splitRow = (line) =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const isTableSeparator = (line) =>
  !!line && line.includes("|") && line.includes("-") && /^[\s|:-]+$/.test(line);

function dedent(lines) {
  const indents = lines
    .filter((line) => line.trim())
    .map((line) => line.match(/^\s*/)[0].length);
  const min = indents.length ? Math.min(...indents) : 0;
  return lines.map((line) => line.slice(min));
}

function renderList(listLines, keyPrefix) {
  const markerIndents = listLines
    .filter((line) => LIST_RE.test(line))
    .map((line) => LIST_RE.exec(line)[1].length);
  const base = Math.min(...markerIndents);

  const items = [];
  let ordered = false;
  listLines.forEach((line) => {
    const match = LIST_RE.exec(line);
    if (match && match[1].length <= base + 1) {
      if (!items.length) ordered = /\d/.test(match[2]);
      items.push({ text: match[3], rest: [] });
    } else if (items.length) {
      items[items.length - 1].rest.push(line);
    }
  });

  const Tag = ordered ? "ol" : "ul";
  return (
    <Tag key={keyPrefix} style={S.ul}>
      {items.map((item, idx) => (
        <li key={`${keyPrefix}-li${idx}`} style={S.li}>
          {parseInline(item.text, `${keyPrefix}-li${idx}`)}
          {item.rest.length ? renderBlocks(dedent(item.rest), `${keyPrefix}-li${idx}-sub`) : null}
        </li>
      ))}
    </Tag>
  );
}

function renderBlocks(lines, keyPrefix) {
  const nodes = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const id = `${keyPrefix}-b${key++}`;

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Fenced code block
    if (/^\s*(```|~~~)/.test(line)) {
      const fence = line.trim().slice(0, 3);
      const buffer = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== fence) {
        buffer.push(lines[i]);
        i += 1;
      }
      i += 1;
      nodes.push(
        <pre key={id} style={S.pre}>
          <code>{buffer.join("\n")}</code>
        </pre>
      );
      continue;
    }

    // Horizontal rule
    if (/^\s*(\*{3,}|-{3,}|_{3,})\s*$/.test(line)) {
      nodes.push(<hr key={id} style={S.hr} />);
      i += 1;
      continue;
    }

    // Heading
    const heading = /^\s{0,3}(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      nodes.push(
        React.createElement(
          `h${level}`,
          { key: id, style: headingStyle(level) },
          parseInline(heading[2], id)
        )
      );
      i += 1;
      continue;
    }

    // Table
    if (line.includes("|") && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].trim() && lines[i].includes("|")) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      nodes.push(
        <div key={id} style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                {header.map((cell, idx) => (
                  <th key={`${id}-h${idx}`} style={S.th}>
                    {parseInline(cell, `${id}-h${idx}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, r) => (
                <tr key={`${id}-r${r}`}>
                  {row.map((cell, c) => (
                    <td key={`${id}-r${r}c${c}`} style={S.cell}>
                      {parseInline(cell, `${id}-r${r}c${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Blockquote
    if (/^\s*>/.test(line)) {
      const buffer = [];
      while (i < lines.length && /^\s*>/.test(lines[i])) {
        buffer.push(lines[i].replace(/^\s*>\s?/, ""));
        i += 1;
      }
      nodes.push(
        <blockquote key={id} style={S.quote}>
          {renderBlocks(buffer, id)}
        </blockquote>
      );
      continue;
    }

    // List (items plus their indented continuation lines)
    if (LIST_RE.test(line)) {
      const buffer = [];
      while (i < lines.length) {
        const current = lines[i];
        if (LIST_RE.test(current) || (current.trim() && /^\s{2,}/.test(current))) {
          buffer.push(current);
          i += 1;
          continue;
        }
        break;
      }
      nodes.push(renderList(buffer, id));
      continue;
    }

    // Paragraph
    const buffer = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !LIST_RE.test(lines[i]) &&
      !/^\s*>/.test(lines[i]) &&
      !/^\s{0,3}#{1,6}\s/.test(lines[i]) &&
      !/^\s*(```|~~~)/.test(lines[i]) &&
      !/^\s*(\*{3,}|-{3,}|_{3,})\s*$/.test(lines[i])
    ) {
      buffer.push(lines[i]);
      i += 1;
    }
    nodes.push(
      <p key={id} style={S.p}>
        {withBreaks(buffer, id)}
      </p>
    );
  }

  return nodes;
}

export default function MarkdownPreview({ source, style }) {
  if (source == null || `${source}`.trim() === "") return null;
  const lines = `${source}`.replace(/\r\n?/g, "\n").split("\n");
  const nodes = renderBlocks(lines, "md");
  // Drop the trailing block's bottom margin so the preview sits flush inside
  // whatever container it is rendered in.
  const last = nodes.length - 1;
  const trimmed = nodes.map((node, idx) =>
    idx === last && React.isValidElement(node)
      ? React.cloneElement(node, {
          style: { ...(node.props.style || {}), marginBottom: 0 },
        })
      : node
  );
  return <div style={{ ...S.root, ...style }}>{trimmed}</div>;
}
